import { Suspense, lazy } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { useUiModules } from './core/config/useUiModules';
import { LayoutShell } from './core/layout/LayoutShell';
import Dashboard from './pages/Dashboard';
import Home from './pages/Home';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import PagesList from './pages/PagesList';
import PageRunner from './pages/PageRunner';
import WorkflowsList from './pages/WorkflowsList';
import WorkflowRunner from './pages/WorkflowRunner';
import CatalogPage from './pages/Catalog';
import FormRunner from './pages/FormRunner';
import { useAppAuthorization } from './core/auth/useAppAuthorization';
import { DynamicRoutes } from './plugins/DynamicRoutes';
import ThemeDemo from './pages/ThemeDemo';
import PluginManagerPage from './pages/PluginManager';

const SecureActionPage = lazy(() => import('./pages/SecureAction/SecureActionPage'));

export default function App() {
  const { modules, isLoading } = useUiModules();
  const appAuth = useAppAuthorization();

  return (
    <LayoutShell loading={isLoading} modules={modules}>
        <Suspense fallback={<div className="glass-card p-4" role="status" aria-live="polite" data-testid="experience-route-loading">Loading…</div>}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/" element={<Home />} />
            {/* Legacy /tasks page superseded by the task-center plugin; redirect
                any stale links/bookmarks to the real inbox. */}
            <Route path="/tasks" element={<Navigate to="/task-center" replace />} />
            <Route path="/theme-demo" element={<ThemeDemo />} />
            <Route path="/app-center/*" element={<PluginManagerPage />} />
            <Route path="/action/:resumeHandle" element={<SecureActionPage />} />
            {modules.workflow && (
              <>
                <Route path="/workflows" element={appAuth.workflows.authorized ? <WorkflowsList /> : <Navigate to="/" replace />} />
                <Route path="/run/workflow/:workflowId" element={appAuth.workflows.authorized ? <WorkflowRunner /> : <Navigate to="/" replace />} />
                <Route path="/catalog" element={<CatalogPage />} />
                <Route path="/form" element={<FormRunner />} />
              </>
            )}
            {modules.crud && (
              <>
                <Route path="/pages" element={<PagesList />} />
                <Route path="/run/page/:pageId" element={<PageRunner />} />
              </>
            )}
            {/* Dynamic plugin routes mounted last so core routes take precedence */}
            <Route path="/*" element={<DynamicRoutes />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </LayoutShell>
  );
}
